import { clampCenterOffsetPx } from '../../core/dubbing/layout.ts';
import {
  computeShadowingBaseline,
  nudgeSubYByDelta,
  resolveSubYKey,
  snapSubY,
  type SubYStorageKey,
} from '../../core/layout.ts';
import type { Settings } from '../../core/settings/schema.ts';
import type { ViewportRect } from '../../ports/VideoController.ts';
import { getPageRuntime } from '../../shared/pageRuntime.ts';

export type SubtitlePositionDragDeps = {
  getSettings: () => Pick<Settings, 'subY' | 'mainSubY' | 'secondSubY' | 'subtitleSeparation'>;
  getViewportRect: () => ViewportRect | null | undefined;
  /** Live paint update (runtime storage snapshot). */
  applyLocal: (patch: Partial<Settings>) => void;
  /** Persist to chrome.storage.sync via content bridge. */
  persist: (patch: Partial<Settings>) => void;
  /** Shadowing: horizontal drag moves the temporary center line (not persisted). */
  isShadowingMode?: () => boolean;
  getShadowingCenterOffsetPx?: (slot: 'main' | 'second') => number;
  setShadowingCenterOffsetPx?: (slot: 'main' | 'second', offsetPx: number) => void;
};

export const SUBTITLE_DRAG_HANDLE_CLASS = 'unisubs-sub-drag-handle';

const DRAG_THRESHOLD_PX = 3;

/** Slot whose handle is mid-drag (survives replaceChildren re-creates). */
let draggingSlot: 'main' | 'second' | null = null;

export function getSubtitlePositionDraggingSlot(): 'main' | 'second' | null {
  return draggingSlot;
}

type DragState = {
  key: SubYStorageKey;
  slot: 'main' | 'second';
  startClientX: number;
  startClientY: number;
  startValue: number;
  startCenterOffsetPx: number;
  shadowing: boolean;
  pointerId: number;
  active: boolean;
  handle: HTMLElement;
};

function letterboxViewWidth(
  settings: Pick<Settings, 'subY' | 'mainSubY' | 'secondSubY' | 'subtitleSeparation'>,
  rect: ViewportRect | null | undefined,
): number {
  if (!rect) return 0;
  let flush = false;
  try {
    flush = getPageRuntime().site === 'yt';
  } catch {
    flush = false;
  }
  return computeShadowingBaseline(rect, 'mainSubtitle', {
    subtitleSeparation: Boolean(settings.subtitleSeparation),
    subY: Number(settings.subY ?? 0.8),
    mainSubY: Number(settings.mainSubY ?? 0.8),
    secondSubY: Number(settings.secondSubY ?? 0.8),
  }, { flushToPlaybackBox: flush }).viewWidth;
}

/** Hover-revealed grip above #mainSub / #secondSub (does not intercept cue text). */
export function createSubtitleDragHandle(slot?: 'main' | 'second'): HTMLButtonElement {
  const handle = document.createElement('button');
  handle.type = 'button';
  handle.className = SUBTITLE_DRAG_HANDLE_CLASS;
  handle.setAttribute('aria-label', 'Adjust subtitle position');
  handle.tabIndex = -1;

  const grip = document.createElement('span');
  grip.className = 'unisubs-sub-drag-grip';
  grip.setAttribute('aria-hidden', 'true');
  for (let i = 0; i < 6; i += 1) {
    grip.appendChild(document.createElement('span'));
  }
  handle.appendChild(grip);

  if (slot && draggingSlot === slot) {
    handle.classList.add('is-dragging');
  }
  return handle;
}

/**
 * Vertical drag via the hover handle updates subY (or separated main/secondSubY),
 * clamped to popup [0, 1] and snapped to step 0.01 on release.
 *
 * Horizontal center-line nudge is shadowing-only (`isShadowingMode`); other
 * practice modes keep vertical-only behavior even when offset deps are wired.
 */
export function installSubtitlePositionDrag(deps: SubtitlePositionDragDeps): () => void {
  let drag: DragState | null = null;

  const markDraggingVisual = (handle: HTMLElement, on: boolean) => {
    handle.classList.toggle('is-dragging', on);
    // Re-created handles during paint: stamp all current-slot grips.
    if (drag) {
      draggingSlot = on ? drag.slot : null;
      document.querySelectorAll(`#${drag.slot === 'main' ? 'mainSub' : 'secondSub'} .${SUBTITLE_DRAG_HANDLE_CLASS}`)
        .forEach((node) => node.classList.toggle('is-dragging', on));
    } else if (!on) {
      draggingSlot = null;
      document.querySelectorAll(`.${SUBTITLE_DRAG_HANDLE_CLASS}.is-dragging`)
        .forEach((node) => node.classList.remove('is-dragging'));
    }
  };

  const applyShadowingCenterOffset = (
    slot: 'main' | 'second',
    startOffsetPx: number,
    dx: number,
  ) => {
    // Re-check live mode so a mid-drag mode switch cannot leak into watch/etc.
    if (!deps.isShadowingMode?.() || !deps.setShadowingCenterOffsetPx) return;
    const viewWidth = letterboxViewWidth(deps.getSettings(), deps.getViewportRect());
    deps.setShadowingCenterOffsetPx(slot, clampCenterOffsetPx(startOffsetPx + dx, viewWidth));
  };

  const onPointerMove = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dx = event.clientX - drag.startClientX;
    const dy = event.clientY - drag.startClientY;
    if (!drag.active) {
      // Non-shadowing: ignore horizontal jitter — same vertical-only threshold as before.
      const distance = drag.shadowing
        ? Math.max(Math.abs(dx), Math.abs(dy))
        : Math.abs(dy);
      if (distance < DRAG_THRESHOLD_PX) return;
      drag.active = true;
      markDraggingVisual(drag.handle, true);
    }
    event.preventDefault();
    const next = nudgeSubYByDelta(drag.startValue, dy, deps.getViewportRect());
    deps.applyLocal({ [drag.key]: next } as Partial<Settings>);
    if (drag.shadowing) applyShadowingCenterOffset(drag.slot, drag.startCenterOffsetPx, dx);
  };

  const endDrag = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.pointerId) return;
    const key = drag.key;
    const slot = drag.slot;
    const startValue = drag.startValue;
    const startClientX = drag.startClientX;
    const startClientY = drag.startClientY;
    const startCenterOffsetPx = drag.startCenterOffsetPx;
    const shadowing = drag.shadowing;
    const handle = drag.handle;
    try {
      handle.releasePointerCapture?.(event.pointerId);
    } catch {
      // ignore — capture may already be cleared after DOM replace
    }
    drag = null;
    markDraggingVisual(handle, false);
    window.removeEventListener('pointermove', onPointerMove, true);
    window.removeEventListener('pointerup', endDrag, true);
    window.removeEventListener('pointercancel', endDrag, true);

    event.preventDefault();
    const dy = event.clientY - startClientY;
    const next = snapSubY(nudgeSubYByDelta(startValue, dy, deps.getViewportRect()));
    const patch = { [key]: next } as Partial<Settings>;
    deps.applyLocal(patch);
    // Always persist on release so chrome.storage matches the final snapped value.
    deps.persist(patch);
    // Center-line offset is session-only — never write it to storage.
    if (shadowing) {
      applyShadowingCenterOffset(slot, startCenterOffsetPx, event.clientX - startClientX);
    }
  };

  const onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    const target = event.target as Element | null;
    const handle = target?.closest?.(`.${SUBTITLE_DRAG_HANDLE_CLASS}`) as HTMLElement | null;
    if (!handle) return;
    const container = handle.closest('#mainSub, #secondSub') as HTMLElement | null;
    if (!container) return;

    event.preventDefault();
    event.stopPropagation();

    const settings = deps.getSettings();
    const slot = container.id === 'mainSub' ? 'main' : 'second';
    const key = resolveSubYKey(slot, Boolean(settings.subtitleSeparation));
    const shadowing = Boolean(deps.isShadowingMode?.());
    drag = {
      key,
      slot,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startValue: Number(settings[key] ?? 0.8),
      startCenterOffsetPx: shadowing ? (deps.getShadowingCenterOffsetPx?.(slot) ?? 0) : 0,
      shadowing,
      pointerId: event.pointerId,
      active: false,
      handle,
    };
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      // happy-dom / older engines may lack capture
    }
    window.addEventListener('pointermove', onPointerMove, true);
    window.addEventListener('pointerup', endDrag, true);
    window.addEventListener('pointercancel', endDrag, true);
  };

  document.addEventListener('pointerdown', onPointerDown, true);
  return () => {
    document.removeEventListener('pointerdown', onPointerDown, true);
    window.removeEventListener('pointermove', onPointerMove, true);
    window.removeEventListener('pointerup', endDrag, true);
    window.removeEventListener('pointercancel', endDrag, true);
    drag = null;
    draggingSlot = null;
  };
}

export function resolvePageExId(): string {
  return document.getElementById('unisubs-container')?.getAttribute('exId') ?? '';
}

export function postSaveSettingsPatch(exId: string, patch: Partial<Settings>): void {
  const resolved = exId || resolvePageExId();
  window.postMessage({ direction: 'save-settings', exId: resolved, message: patch }, '*');
}
